import React, { useEffect, useState } from "react";
import { Spinner } from "@skbkontur/react-ui";
import { Progress } from "@skbkontur/extern-core-js/dist/cjs/src/common/progress";
import { CancellationToken } from "@skbkontur/extern-core-js/dist/cjs/src/common/cancel";

import { useAsyncEffect } from "../../../../../../../../Common/UseAsyncEffect";
import { uploadToDrive } from "../../../../../../Helpers/file";
import { FileProps, UploadFileStatuses } from "../Types";
import { isNotNullOrEmpty, noop } from "../../../../../../../../Common/TypingUtils";
import { Icon } from "../../../../../../Controls/Icons/Icon";
import { useMetrics } from "../../../../../../Controls/Internals/Metrics/MetricsProviderContext";
import { EXCEL_UPLOAD_DELETE_FILE_FROM_LOAD } from "../../../../../../Controls/metricsAction";
import { cropFileName } from "../HintUtils";
import { uploadWithExternDriveClient } from "../../../../../../Helpers/ExternDriveClient/uploadWithExternDriveClient";
import { getCandyHub } from "../../../../../../Forms/Sentry/InitializeSentry";

import { FileCountInstances, FileName, FileTitle, RightIconContainer, Root } from "./UploadFile.styles";

interface UploadFileProps {
    file: FileProps;
    removeFile: (fileToRemove: FileProps) => void;
    setNewFileProps: (nextFileProps: FileProps) => void;
    pathToTable: string;
    getPortalUserId?: () => Promise<string>;
    useExternDriveClient?: boolean;
}

export const UploadFile: React.FC<UploadFileProps> = ({
    removeFile,
    setNewFileProps,
    pathToTable,
    file,
    getPortalUserId,
    useExternDriveClient,
}) => {
    const track = useMetrics();
    const [statusIcon, setStatusIcon] = useState(<Spinner type="mini" dimmed caption="" />);
    const [hovered, setHovered] = useState(false);
    const [progressPercents, setProgressPercents] = useState<number | null>(null);

    useAsyncEffect(async () => {
        if (isNotNullOrEmpty(file.driveUri)) {
            return;
        }

        try {
            const upload = useExternDriveClient ? uploadUseExternDriveClient : uploadUseOldDriveClient;
            const uploadedFileUrl: string | undefined = await upload();

            if (uploadedFileUrl) {
                onUploadHandler(uploadedFileUrl);
            }
        } catch (e) {
            setNewFileProps({
                id: file.id,
                uploadFile: file.uploadFile,
                status: UploadFileStatuses.error,
                errorMessage: <p>Произошла ошибка при загрузке файла</p>,
            });
            getCandyHub().captureException(e);
        }
    }, []);

    useEffect(() => {
        if (file.status === UploadFileStatuses.progress || file.status === UploadFileStatuses.new) {
            setStatusIcon(<Spinner type="mini" dimmed caption="" />);
        } else if (hovered) {
            setStatusIcon(<Icon name={"XIcon"} />);
        } else if (file.status === UploadFileStatuses.error) {
            setStatusIcon(<Icon name={"MinusCircleIcon16Solid"} />);
        } else if (file.status === UploadFileStatuses.success) {
            setStatusIcon(<Icon name={"CheckAIcon"} />);
        }
    }, [file.status, hovered]);

    const uploadUseOldDriveClient = async () => {
        const uploadResult = uploadToDrive(file.uploadFile, noop);
        await uploadResult.initDrive;
        const url = await uploadResult.getDriveTask();

        return url;
    };

    const uploadUseExternDriveClient = async () => {
        if (!getPortalUserId) {
            throw new Error("getPortalUserId is missing");
        }

        const getDriveTask = await uploadWithExternDriveClient({
            getPortalUserId: getPortalUserId,
        });

        const progress = new Progress();
        const cancellationToken = new CancellationToken(noop);

        const result = await progress.subscribe(
            getDriveTask({
                file: file.uploadFile,
                progress: progress,
                cancellationToken: cancellationToken,
            }),
            percents => {
                setProgressPercents(percents);
            }
        );

        return result.url;
    };

    const onUploadHandler = (url: string) => {
        setNewFileProps({ id: file.id, uploadFile: file.uploadFile, status: UploadFileStatuses.new, driveUri: url });
    };

    const onRemoveFile = () => {
        track(EXCEL_UPLOAD_DELETE_FILE_FROM_LOAD, { path: pathToTable, fileName: file.uploadFile.name });
        removeFile(file);
    };

    const setHoveredState = () => {
        setHovered(true);
    };

    const setUnHoveredState = () => {
        setHovered(false);
    };

    const isFileInProgress = file.status === UploadFileStatuses.progress;

    return (
        <Root hovered={hovered} onMouseEnter={setHoveredState} onMouseLeave={setUnHoveredState}>
            <FileTitle>
                <FileName status={file.status}>{cropFileName(file.uploadFile.name)}</FileName>
                <RightIconContainer
                    isDefaultCursor={isFileInProgress}
                    data-tid={"FileStatus"}
                    onClick={isFileInProgress ? noop : onRemoveFile}>
                    {statusIcon}
                </RightIconContainer>
            </FileTitle>
            {file.instancesInfo?.count && file.instancesInfo?.description && (
                <FileCountInstances>
                    {file.instancesInfo?.description}
                    <span data-tid={"InstancesCount"}>{file.instancesInfo?.count}</span>
                </FileCountInstances>
            )}
            {file.status === UploadFileStatuses.new && progressPercents !== null && (
                <p>
                    {progressPercents === 0
                        ? "Подготовка файла к загрузке..."
                        : `Загрузка: ${Math.round(progressPercents)}%`}
                </p>
            )}
            {file.status === UploadFileStatuses.progress && progressPercents !== null && <p>Обработка...</p>}
            {file.errorMessage}
            {file.warningMessage}
        </Root>
    );
};
